import {
  Avatar,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { SimpleCheckbox } from 'Components';
import Grid from '@mui/material/Grid';
import { IMedication, MEDICATION_STATUSES } from 'API';
import { getNoteOutOfStockOption } from 'Components/MedicationList';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { PurpleOutlineWarningIcon } from 'Images';

interface IMedicationListItem {
  medication: IMedication;
  handleToggle?: (medication: IMedication) => (e: React.MouseEvent) => void;
  outOfStockMedications: IMedication[];
  checkedMedications: IMedication[];
  showMedicationStatus: boolean;
  index: number;
  isFinalStep?: boolean;
}

export const MedicationListItem = ({
  handleToggle,
  medication,
  outOfStockMedications,
  checkedMedications,
  index,
  isFinalStep,
}: IMedicationListItem) => {
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby,
  );

  return isDesktop ? (
    <ListItem sx={{ textAlign: 'right' }}>
      <Stack
        tabIndex={1}
        direction='row'
        onClick={handleToggle && handleToggle(medication)}
        sx={{
          alignItems: 'center',
          cursor: 'pointer',
          boxShadow: '-2px -2px 12px 0px rgba(8, 30, 84, 0.04)',
          borderRadius: '16px',
          backgroundColor: '#fff',
          p: '16px 24px',
          border: '1px solid',
          borderColor: 'primary.shade_200',
        }}
      >
        {!isFinalStep && (
          <ListItemIcon>
            <SimpleCheckbox
              edge='start'
              checked={
                medication.medstatus === MEDICATION_STATUSES.notAvailable
                  ? outOfStockMedications.find(
                      (outOfStockMedication) =>
                        outOfStockMedication.medicatioN_CODE ===
                        medication.medicatioN_CODE,
                    ) !== undefined
                  : checkedMedications.find(
                      (checkedMedication) =>
                        checkedMedication.medicatioN_CODE ===
                        medication.medicatioN_CODE,
                    ) !== undefined
              }
              tabIndex={-1}
              disableRipple
              slotProps={{ input: { id: `check-med-${index}` } }}
            />
          </ListItemIcon>
        )}
        <ListItemText
          disableTypography
          primary={
            <Grid container>
              <Grid
                size={{
                  xs: 12,
                  md: 5,
                }}
              >
                <label
                  style={{
                    pointerEvents: 'none',
                    fontSize: '18px',
                  }}
                  htmlFor={`check-med-${index}`}
                >
                  {medication.medicatioN_NAME}
                </label>
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 2,
                }}
              >
                <Typography variant='body3'>{medication.dosage}</Typography>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2,
                }}
              >
                <Typography variant='body3'>
                  {medication.doseperday === 1
                    ? dictionary.doseperday
                    : `${medication.doseperday} ${dictionary.doseperdays}`}
                </Typography>
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  md: 2,
                }}
              >
                <Typography variant='body3'>
                  {medication.fornumberdays} {dictionary.days}
                </Typography>
              </Grid>
            </Grid>
          }
        />
      </Stack>
    </ListItem>
  ) : (
    <ListItem sx={{ textAlign: 'right' }}>
      <Stack
        tabIndex={1}
        direction='column'
        onClick={handleToggle && handleToggle(medication)}
        sx={{
          cursor: 'pointer',
          boxShadow: '-2px -2px 12px 0px rgba(8, 30, 84, 0.04)',
          borderRadius: '16px',
          backgroundColor: '#fff',
          p: '16px 16px 24px',
          border: '1px solid',
          borderColor: 'primary.shade_200',
        }}
      >
        <Stack>
          <Typography variant='body3'>
            {dictionary.nameOfTheMedication}
          </Typography>
          <Stack
            sx={{
              flexDirection: 'row',
              alignItems: 'start',
            }}
          >
            {!isFinalStep && (
              <SimpleCheckbox
                edge='start'
                checked={
                  medication.medstatus === MEDICATION_STATUSES.notAvailable
                    ? outOfStockMedications.find(
                        (outOfStockMedication) =>
                          outOfStockMedication.medicatioN_CODE ===
                          medication.medicatioN_CODE,
                      ) !== undefined
                    : checkedMedications.find(
                        (checkedMedication) =>
                          checkedMedication.medicatioN_CODE ===
                          medication.medicatioN_CODE,
                      ) !== undefined
                }
                tabIndex={-1}
                disableRipple
                slotProps={{ input: { id: `check-med-${index}` } }}
              />
            )}
            <label
              style={{
                pointerEvents: 'none',
                fontSize: '20px',
                marginTop: '8px',
              }}
              htmlFor={`check-med-${index}`}
            >
              {medication.medicatioN_NAME}
            </label>
          </Stack>
        </Stack>

        <Divider
          aria-hidden='true'
          sx={{ borderColor: 'primary.shade_100', width: '100%', m: '16px 0' }}
        />
        <Typography variant='body3'>{dictionary.dosageText}</Typography>
        <Typography variant='body2'>
          {medication.dosage} {dictionary.units}{' '}
          {medication.doseperday === 1
            ? dictionary.doseperday
            : `${medication.doseperday} ${dictionary.doseperdays}`}{' '}
          {dictionary.for} {medication.fornumberdays} {dictionary.days}
        </Typography>

        {isFinalStep &&
          medication.medstatus === MEDICATION_STATUSES.notAvailable && (
            <Grid
              container
              sx={{
                alignItems: 'center',
                gap: 1,
                flexWrap: 'nowrap',
                mt: 2,
              }}
              size={{
                xs: 12,
                md: 3,
              }}
            >
              <Grid
                sx={{
                  alignSelf: 'flex-start',
                  mt: '5px',
                }}
              >
                <Avatar
                  alt={''}
                  src={PurpleOutlineWarningIcon}
                  sx={{
                    width: '20px',
                    height: '20px',
                    alignSelf: 'flex-start',
                    position: 'relative',
                    top: '-3px',
                  }}
                />
              </Grid>
              <Grid>
                <Typography variant='body6' component={'p'}>
                  {dictionary.notInStock}
                </Typography>
                <Typography variant='body6' component={'p'}>
                  {medication.OutOfStockOptionCode &&
                    getNoteOutOfStockOption(medication)}
                </Typography>
              </Grid>
            </Grid>
          )}
      </Stack>
    </ListItem>
  );
};
